import d from 'debug';
import { createElementWithContext } from 'fluxible-addons-react';
import React from 'react';
import ReactDOM from 'react-dom';
import { I18nextProvider } from 'react-i18next';

import { extractLocale } from '../client/util/locale';
import { initI18n } from '../lib/i18n';
import { loadTranslations } from '../lib/i18n/translation-loader';
import app from './app';
import DocumentService from './services/DocumentService.client';
import LoggingService from './services/LoggingService.client';
import NavigationService from './services/NavigationService.client';
import ApplicationStore from './stores/ApplicationStore';

require('./styles/docs.styl');

const debug = d('docs:react');
const dehydratedState = window.App; // Sent from the server

window.React = React; // For chrome dev tool support

// expose debug object to browser, so that it can be enabled/disabled from browser:
// https://github.com/visionmedia/debug#browser-support
window.fluxibleDebug = debug;

// Register services
const plugin = app.getPlugin('ServiceProxyPlugin');
plugin.registerService('DocumentService', DocumentService);
plugin.registerService('LoggingService', LoggingService);
plugin.registerService('NavigationService', NavigationService);
plugin.registerService('FetchService', window.fetch);

debug('rehydrating app');

// Get the stored language from local storage
const userPreferredLanguage = extractLocale(window.location.pathname, window.location.search);

const initializeI18n = async () => {
  const appStore = window.context.getStore(ApplicationStore);
  const isDocsI18nEnabled = appStore.getIsDocsI18nEnabled();

  await initI18n({
    loader: loadTranslations,
    initialNS: ['core'],
    enabled: isDocsI18nEnabled,
    initialLanguage: userPreferredLanguage,
  });
};

// pass in the dehydrated server state from server.js
app.rehydrate(dehydratedState, async (err, context) => {
  if (err) {
    throw err;
  }

  window.context = context;

  await initializeI18n();

  const mountPoint = document.getElementById('app');

  debug('React Rendering');
  ReactDOM.hydrate(
    <I18nextProvider i18n={window.i18next}>{createElementWithContext(context)}</I18nextProvider>,
    mountPoint,
    () => {
      debug('React Rendered');
      // Remove SSR styles once client-side rendering takes over
      const ssrStyleEl = document.getElementById('jss-server-side');
      if (ssrStyleEl) {
        ssrStyleEl.parentNode.removeChild(ssrStyleEl);
      }
    }
  );
});
